import { useState, useRef, useEffect } from "react";
import ReactPlayerLoader from "@brightcove/react-player-loader";
import { WebVTTParser } from "webvtt-parser";
import SubtitleEditor from "./Components/SubtitleEditor-2";
import SubtitleEditorLoader from "./Components/SubtitleEditorLoader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Backdrop, CircularProgress, Box } from '@mui/material';

const VideoPlayer = ({ brightcoveVideoId, tracks, videoTitle, courseTitle }) => {
    const [subtitles, setSubtitles] = useState([]);
    const [selectedSubtitle, setSelectedSubtitle] = useState(null);
    const [vttTree, setVttTree] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlayerReady, setIsPlayerReady] = useState(false);
    const [playerInstance, setPlayerInstance] = useState(null);
    const subtitleEditorRef = useRef();
    const [isPlaying, setIsPlaying] = useState(false);
    const [isSubtitleLoading, setIsSubtitleLoading] = useState(false);
    const [replay, setReplay] = useState(false);
    const [replayEndTime, setReplayEndTime] = useState(null);
    const [duration, setDuration] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef();

    useEffect(() => {
        const fetchTracks = async () => {
            if (tracks && tracks.length > 0) {
                try {
                    const subtitleData = await Promise.all(
                        tracks.map(async (trackUrl) => {
                            const response = await fetch(
                                `${process.env.REACT_APP_API_ENTRYPOINT}/tracks/${trackUrl.split('/').pop()}`,
                                {
                                    headers: {
                                        'Authorization': `Bearer ${localStorage.getItem("token")}`
                                    }
                                }
                            );
                            if (!response.ok) throw new Error(`Failed to fetch track ${trackUrl}`);
                            const data = await response.json();
                            return {
                                label: data.label,
                                src: data.mediaUrl || data.media_url,
                                language: data.label,
                                id: data.id,
                            };
                        })
                    );
                    setSubtitles(subtitleData);
                    const arabicSubtitle = subtitleData.find((sub) => sub.language === "ar");
                    if (arabicSubtitle) {
                        await handleSubtitleSelect(arabicSubtitle);
                    } else if (subtitleData.length > 0) {
                        await handleSubtitleSelect(subtitleData[0]);
                    }
                } catch (error) {
                    console.error("Error fetching tracks:", error);
                }
            }
        };
        fetchTracks();
    }, [tracks]);

    const fetchSubtitleFile = async (subtitleUrl) => {
        try {
            const response = await fetch(subtitleUrl, {
                cache: 'reload'
            });
            if (!response.ok) throw new Error("Failed to load subtitle file");
            const text = await response.text();
            parseVTT(text);
        } catch (error) {
            console.error("Error fetching subtitle file:", error);
        }
    };

    const padZero = (number, config = {}) => {
        const { length = 2 } = config;
        const numberString = number.toString();
        if (numberString.length < length) {
            const padLength = length - numberString.length;
            let zeroString = "";
            for (let index = 0; index < padLength; index++) {
                zeroString = `0${zeroString}`;
            }
            return `${zeroString}${numberString}`;
        }
        if (numberString.length > length) {
            return numberString.replace(/^0+/, "");
        }
        return numberString;
    };

    const parseVTT = (vttText) => {
        // Remove BOM and any leading whitespace that might break the strict WebVTT parser
        let cleanText = vttText.replace(/^\uFEFF/, '').trimStart();
        
        // Fallback: If it's missing the WEBVTT header (e.g., if it's raw cues), add it
        if (!cleanText.startsWith('WEBVTT')) {
            cleanText = 'WEBVTT\n\n' + cleanText;
        }

        const parser = new WebVTTParser();
        const parsedVTT = parser.parse(cleanText, "metadata");

        if (parsedVTT.errors.length > 0 && parsedVTT.cues.length === 0) {
            console.error("VTT Parsing Errors:", parsedVTT.errors);
            toast.error("Invalid VTT file format. Please check the file and try again.");
            return;
        }

        parsedVTT.cues = parsedVTT.cues.map((cue, index) => {
            const startDate = new Date(cue.startTime * 1000);
            const endDate = new Date(cue.endTime * 1000);
            return {
                ...cue,
                text: cue.text,
                startTime: cue.startTime,
                endTime: cue.endTime,
                startHours: padZero(startDate.getUTCHours()),
                startMinutes: padZero(startDate.getUTCMinutes()),
                startSeconds: padZero(startDate.getUTCSeconds()),
                startMilliseconds: padZero(startDate.getUTCMilliseconds(), { length: 3 }),
                endHours: padZero(endDate.getUTCHours()),
                endMinutes: padZero(endDate.getUTCMinutes()),
                endSeconds: padZero(endDate.getUTCSeconds()),
                endMilliseconds: padZero(endDate.getUTCMilliseconds(), { length: 3 }),
                index
            };
        });

        setVttTree(parsedVTT);
    };

    const onSuccess = (success) => {
        const player = success.ref;
        setPlayerInstance(player);
        setIsPlayerReady(true);

        player.on("timeupdate", () => {
            setCurrentTime(player.currentTime());
        });

        player.on("play", () => {
            setIsPlaying(true);
        });

        player.on("pause", () => {
            setIsPlaying(false);
        });

        player.on('loadedmetadata', () => {
            setDuration(player.duration());
        });

        setIsPlaying(!player.paused());
    };

    const onFailure = (error) => {
        console.error("Brightcove Player Error:", error);
        setIsPlayerReady(false);
    };

    const handleSubtitleSelect = async (subtitle) => {
        setIsSubtitleLoading(true);
        setSelectedSubtitle(subtitle);
        await fetchSubtitleFile(subtitle.src);
        setIsSubtitleLoading(false);
    };

    const handleImportClick = () => {
        if (!selectedSubtitle) {
            toast.error("Please select a subtitle before importing.");
            return;
        }
        if (window.confirm("Importing a new VTT file will discard any unsaved changes. Are you sure?")) {
            fileInputRef.current.click();
        }
    };

    const handleImportVTT = (event) => {
        const file = event.target.files[0];
        if (!selectedSubtitle) {
            toast.error("Please select a subtitle before importing.");
            event.target.value = null;
            return;
        }
        if (file && file.name.endsWith('.vtt')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                parseVTT(text);
                if (selectedSubtitle) {
                    setSelectedSubtitle({
                        ...selectedSubtitle,
                        src: file.name
                    });
                    toast.success("Subtitles imported successfully.");
                }
            };
            reader.onerror = () => {
                toast.error("Failed to read VTT file. Please try again.");
            };
            reader.readAsText(file);
        } else {
            toast.error("Please select a valid .vtt file.");
        }
        event.target.value = null;
    };

    const handleSeekAndPlay = (time) => {
        if (playerInstance) {
            playerInstance.currentTime(time);
            playerInstance.play().catch((error) => {
                if (error.name === 'AbortError') {
                    console.log('Play request was interrupted by pause');
                } else {
                    console.error('Error playing video:', error);
                }
            });
        }
    };

    const handlePause = () => {
        if (playerInstance) {
            playerInstance.pause();
        }
    };

    const handleReplay = async (startTime, endTime) => {
        if (playerInstance) {
            try {
                setReplay(true);
                setReplayEndTime(endTime);
                playerInstance.currentTime(startTime);
                setCurrentTime(playerInstance.currentTime());
                await playerInstance.play();
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Play request was interrupted by pause');
                } else {
                    console.error("Error during replay:", error);
                }
                setReplay(false);
                setReplayEndTime(null);
            }
        }
    };

    useEffect(() => {
        if (replay && replayEndTime !== null && currentTime >= replayEndTime - 0.1) {
            handlePause();
            playerInstance.currentTime(replayEndTime - 0.1);
            setReplay(false);
            setReplayEndTime(null);
        }
    }, [currentTime, replay, replayEndTime]);

    const generateVttString = (list) => {
        const formatTime = (hours, minutes, seconds, milliseconds) => {
            return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}.${padZero(milliseconds, { length: 3 })}`;
        };
        return (
            "WEBVTT\n\n" +
            list
                .map((cue) => {
                    const start = formatTime(
                        cue.startHours,
                        cue.startMinutes,
                        cue.startSeconds,
                        cue.startMilliseconds
                    );
                    const end = formatTime(
                        cue.endHours,
                        cue.endMinutes,
                        cue.endSeconds,
                        cue.endMilliseconds
                    );
                    return `${start} --> ${end}\n${cue.text}\n`;
                })
                .join("\n")
        );
    };

    const handleSaveSubtitles = async (data) => {
        setIsSaving(true);
        const toastId = toast.loading("Saving subtitles...");
        try {
            const vttString = generateVttString(data.list);
            const blob = new Blob([vttString], { type: "text/vtt" });
            const formData = new FormData();
            formData.append("file", blob, selectedSubtitle.src.split('/').pop());

            const response = await fetch(
                `${process.env.REACT_APP_API_ENTRYPOINT}/tracks/${selectedSubtitle.id}/update`,
                {
                    method: "POST",
                    body: formData,
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem("token")}`
                    }
                }
            );

            if (response.ok) {
                await response.json();
                toast.update(toastId, {
                    render: "Subtitles saved successfully!",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                });
            } else {
                throw new Error("Upload failed");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.update(toastId, {
                render: "There is currently a technical problem. The file has been downloaded instead.",
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
            handleDownload();
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownload = () => {
        if (subtitleEditorRef.current) {
            const list = subtitleEditorRef.current.getCurrentData();
            const vttString = generateVttString(list);
            const blob = new Blob([vttString], { type: "text/vtt" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${videoTitle || 'subtitles'}.vtt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    const handleSaveClick = () => {
        if (subtitleEditorRef.current) {
            subtitleEditorRef.current.save();
        }
    };

    if (!brightcoveVideoId) return <p>No video available</p>;

    return (
        <>
            <ToastContainer />
            <div className="video-player-container">
                <div className="video-title">
                    <h3>{videoTitle} | {courseTitle}</h3>
                </div>
                {!isPlayerReady && (
                    <div className="loading-state">
                        <div className="loading-spinner" />
                        <p>Loading video...</p>
                    </div>
                )}

                <div className="video-content">
                    <div className="video-layout">
                        <div className="video-section">
                            <div className="video-wrapper">
                                <ReactPlayerLoader
                                    accountId="6164421959001"
                                    playerId="default"
                                    videoId={brightcoveVideoId}
                                    onFailure={onFailure}
                                    onSuccess={onSuccess}
                                    options={{
                                        responsive: true,
                                        autoplay: false,
                                    }}
                                />
                            </div>
                            {subtitles.length > 0 && (
                                <div className="subtitles-selector">
                                    <h3>Available Subtitles</h3>
                                    <div className="subtitle-buttons">
                                        {subtitles.map((subtitle, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleSubtitleSelect(subtitle)}
                                                className={`subtitle-button ${selectedSubtitle?.id === subtitle.id ? 'active' : ''}`}
                                            >
                                                {subtitle.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selectedSubtitle && vttTree && (
                                <div>
                                    <button
                                        className="save-button"
                                        onClick={handleSaveClick}
                                        disabled={!vttTree || isSaving}
                                    >
                                        Save Changes
                                    </button>
                                    <button
                                        className="download-button"
                                        onClick={handleDownload}
                                        disabled={!vttTree}
                                    >
                                        Download Subtitle
                                    </button>
                                    <button
                                        className="import-button"
                                        onClick={handleImportClick}
                                        disabled={isSaving}
                                    >
                                        Import Subtitle
                                    </button>
                                    <input
                                        type="file"
                                        accept=".vtt"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleImportVTT}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="subtitles-section">
                            {(!tracks || tracks.length === 0) ? (
                                <p className="not-found-message">The subtitle is not available yet.</p>
                            ) : selectedSubtitle ? (
                                <div className="subtitle-editor-container">
                                    <div className="subtitle-editor-wrapper">
                                        {isSubtitleLoading ? (
                                            <SubtitleEditorLoader />
                                        ) : vttTree ? (
                                            <SubtitleEditor
                                                ref={subtitleEditorRef}
                                                onSubmit={handleSaveSubtitles}
                                                list={vttTree.cues}
                                                onSeekAndPlay={handleSeekAndPlay}
                                                onPause={handlePause}
                                                currentTime={currentTime}
                                                isPlaying={isPlaying}
                                                onReplay={handleReplay}
                                                duration={duration}
                                            />
                                        ) : (
                                            <p className="not-found-message">The subtitle is not available yet.</p>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            <Backdrop
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    color: '#fff',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                open={isSaving}
            >
                <CircularProgress color="inherit" />
                <Box sx={{ mt: 2, fontSize: '1.2rem' }}>Saving subtitles, please wait...</Box>
            </Backdrop>
        </>
    );
};

export default VideoPlayer;